/**
 * 医疗记录管理 Store（迁移版）
 *
 * 将原有的 src/stores/medicalStore 功能迁移到 clinical-record 模块
 * 同时保持向后兼容，逐步更新引用
 *
 * @module clinical-record/stores/recordStore
 */

import { defineStore } from "pinia";
import { ref, computed, reactive, type Ref, type ComputedRef } from "vue";
import type {
  MedicalRecord,
  VirtualMedicalRecord,
  MedicalRecordField,
  ExtractResult,
  ConsultationMessage,
} from "./types";
import {
  STRING_FIELDS,
  sanitizeMedicalText,
} from "@clinical-record/shared/utils/recordDataNormalizer";
import { type NormalizedRecord } from "@clinical-record/shared/domain/recordAdapter";

/** AI 生成建议快照（一键生成等回填时记录；保存时随请求携带做采纳留痕，不落病历本体） */
export interface AiSuggestedSnapshot {
  /** 生成功能标识，如 one_click_full */
  feature: string;
  /** 字段名 → AI 建议内容 */
  fields: Record<string, string>;
}

/**
 * 清洗病历所有文本字段的空行/尾部空白。
 * 用于修正历史/生成数据里残留的大量空行导致自适应输入框被撑高。
 * 幂等：多次执行结果一致。
 */
function sanitizeRecordText(record: NormalizedRecord): NormalizedRecord {
  const cleaned: NormalizedRecord = { ...record };
  for (const field of STRING_FIELDS) {
    const value = cleaned[field];
    if (typeof value !== "string" || value.length === 0) continue;
    const next = sanitizeMedicalText(value);
    if (next !== value) cleaned[field] = next;
  }
  return cleaned;
}

/**
 * 医疗记录管理 Store - 模块化版本
 */
export const useMedicalStore = defineStore("medicalRecord", () => {
  // ==================== 状态 ====================

  // 当前医疗记录
  const currentMedicalRecord: Ref<NormalizedRecord | null> = ref(null);

  // AI 生成建议快照（一键生成等回填时记录；仅在下一次保存时消费一次）
  const aiSuggested: Ref<AiSuggestedSnapshot | null> = ref(null);

  // 用于填充输入的记录
  const recordToPopulateInput: Ref<MedicalRecord | null> = ref(null);

  // LLM 提取结果
  const currentExtractResult: Ref<ExtractResult | null> = ref(null);

  // 虚拟病历（累积）
  const virtualMedicalRecord = reactive<VirtualMedicalRecord>({
    chief_complaint: "",
    present_illness: "",
    past_history: "",
    allergy_history: "",
    physical_examination_text: "",
    lab_results_text: "",
    imaging_results_text: "",
    other_exams_text: "",
    western_diagnosis_text: "",
    western_treatment_plan: "",
    management_plan: "",
    precautions: "",
    tcm_symptoms: "",
    tcm_diagnosis: "",
    tcm_pattern_text: "",
    tcm_treatment_plan: "",
    tcm_prescription_text: "",
    tcm_health: "",
  });

  // LLM 输入输出
  const inputMessage = ref("");
  const outputContent = ref("");
  const lastLLMResponse = ref("");

  // 处理状态
  const isLoading = ref(false);
  const isTrainingModel = ref(false);
  const isOptimizingPrescription = ref(false);

  // 咨询历史
  const tcmConsultationHistory = ref<ConsultationMessage[]>([]);
  const westernConsultationHistory = ref<ConsultationMessage[]>([]);

  // ==================== 计算属性 ====================

  const virtualMedicalRecordFields: ComputedRef<MedicalRecordField[]> =
    computed(() => {
      return [
        { key: "chief_complaint", label: "主诉", icon: "🎯" },
        { key: "present_illness", label: "现病史", icon: "📋" },
        { key: "past_history", label: "既往史", icon: "📚" },
        { key: "allergy_history", label: "过敏史", icon: "⚠️" },
        { key: "physical_examination_text", label: "体格检查", icon: "🩺" },
        { key: "lab_results_text", label: "实验室检查", icon: "🔬" },
        { key: "imaging_results_text", label: "器械检查", icon: "📷" },
        { key: "western_diagnosis_text", label: "西医诊断", icon: "🏥" },
        { key: "western_treatment_plan", label: "西医治疗", icon: "💊" },
        { key: "management_plan", label: "处理意见", icon: "📝" },
        { key: "precautions", label: "注意事项", icon: "⚡" },
        { key: "tcm_symptoms", label: "四诊资料", icon: "🌿" },
        { key: "tcm_diagnosis", label: "中医诊断", icon: "📖" },
        { key: "tcm_pattern_text", label: "中医证候", icon: "🎭" },
        { key: "tcm_treatment_plan", label: "中医治疗", icon: "🍵" },
        { key: "tcm_health", label: "中医健康管理", icon: "🌱" },
      ] as MedicalRecordField[];
    });

  const hasVirtualMedicalContent: ComputedRef<boolean> = computed(() => {
    return Object.values(virtualMedicalRecord).some((v) => v?.trim());
  });

  const hasCurrentMedicalRecord: ComputedRef<boolean> = computed(() => {
    return !!currentMedicalRecord.value;
  });

  const currentPatientId: ComputedRef<string> = computed(() => {
    return currentMedicalRecord.value?.patient_id || "";
  });

  const virtualRecordFilledCount: ComputedRef<number> = computed(() => {
    return Object.values(virtualMedicalRecord).filter((v) => v?.trim()).length;
  });

  const isAnyLoading: ComputedRef<boolean> = computed(() => {
    return (
      isLoading.value || isTrainingModel.value || isOptimizingPrescription.value
    );
  });

  const hasTcmHistory: ComputedRef<boolean> = computed(() => {
    return tcmConsultationHistory.value.length > 0;
  });

  const hasWesternHistory: ComputedRef<boolean> = computed(() => {
    return westernConsultationHistory.value.length > 0;
  });

  // ==================== Actions ====================

  const setMedicalRecord = (record: NormalizedRecord | null): void => {
    if (!record) {
      currentMedicalRecord.value = null;
      aiSuggested.value = null;
      return;
    }
    // 切换到不同病历时清空 AI 建议快照，避免跨病历污染；
    // 同一病历的 patch 回填（如一键生成）id 不变，不触发清空。
    const prevId = String(
      currentMedicalRecord.value?._id ?? currentMedicalRecord.value?.id ?? "",
    );
    const nextId = String(record?._id ?? record?.id ?? "");
    if (prevId !== nextId) {
      aiSuggested.value = null;
    }
    // 归一化属于 API/表单适配边界；Store 只接受并保存标准病历。
    currentMedicalRecord.value = sanitizeRecordText(record);
  };

  const setAiSuggested = (snapshot: AiSuggestedSnapshot): void => {
    aiSuggested.value = snapshot;
  };

  const clearAiSuggested = (): void => {
    aiSuggested.value = null;
  };

  const setRecordToPopulate = (record: MedicalRecord | null): void => {
    recordToPopulateInput.value = record;
  };

  const setCurrentExtractResult = (
    extractResult: ExtractResult | null,
  ): void => {
    currentExtractResult.value = extractResult;
  };

  const clearCurrentExtractResult = (): void => {
    currentExtractResult.value = null;
  };

  const setVirtualMedicalField = (
    key: keyof VirtualMedicalRecord,
    value: string,
  ): void => {
    virtualMedicalRecord[key] = value;
  };

  const updateVirtualMedicalRecord = (
    updates: Partial<VirtualMedicalRecord>,
  ): void => {
    Object.assign(virtualMedicalRecord, updates);
  };

  const clearVirtualMedicalRecord = (
    key?: keyof VirtualMedicalRecord,
  ): void => {
    if (key) {
      virtualMedicalRecord[key] = "" as any;
    } else {
      Object.keys(virtualMedicalRecord).forEach((k) => {
        virtualMedicalRecord[k as keyof VirtualMedicalRecord] = "" as any;
      });
    }
  };

  const setInputMessage = (message: string): void => {
    inputMessage.value = message;
  };

  const setOutputContent = (content: string): void => {
    outputContent.value = content;
  };

  const setLastLLMResponse = (response: string): void => {
    lastLLMResponse.value = response;
  };

  const clearInputOutput = (): void => {
    inputMessage.value = "";
    outputContent.value = "";
  };

  const setLoading = (loading: boolean): void => {
    isLoading.value = loading;
  };

  const setTrainingModel = (training: boolean): void => {
    isTrainingModel.value = training;
  };

  const setOptimizingPrescription = (optimizing: boolean): void => {
    isOptimizingPrescription.value = optimizing;
  };

  const addTcmMessage = (message: ConsultationMessage): void => {
    tcmConsultationHistory.value.push(message);
  };

  const addWesternMessage = (message: ConsultationMessage): void => {
    westernConsultationHistory.value.push(message);
  };

  const clearTcmHistory = (): void => {
    tcmConsultationHistory.value = [];
  };

  const clearWesternHistory = (): void => {
    westernConsultationHistory.value = [];
  };

  const clearAllConsultationHistory = (): void => {
    clearTcmHistory();
    clearWesternHistory();
  };

  const resetAll = (): void => {
    currentMedicalRecord.value = null;
    recordToPopulateInput.value = null;
    currentExtractResult.value = null;
    clearVirtualMedicalRecord();
    clearAiSuggested();
    setInputMessage("");
    setOutputContent("");
    setLastLLMResponse("");
    setLoading(false);
    setTrainingModel(false);
    setOptimizingPrescription(false);
    clearAllConsultationHistory();
  };

  /**
   * 从虚拟病历创建正式记录
   */
  const createRecordFromVirtual = (
    patientId: string,
  ): Omit<MedicalRecord, "_id"> => {
    const now = new Date().toISOString();
    return {
      patient_id: patientId,
      visit_date: now,
      chief_complaint: virtualMedicalRecord.chief_complaint,
      present_illness: virtualMedicalRecord.present_illness,
      past_history: virtualMedicalRecord.past_history,
      allergy_history: virtualMedicalRecord.allergy_history,
      physical_examination_text: virtualMedicalRecord.physical_examination_text,
      lab_results_text: virtualMedicalRecord.lab_results_text,
      imaging_results_text: virtualMedicalRecord.imaging_results_text,
      other_exams_text: virtualMedicalRecord.other_exams_text,
      western_diagnosis_text: virtualMedicalRecord.western_diagnosis_text,
      western_treatment_plan: virtualMedicalRecord.western_treatment_plan,
      tcm_diagnosis: virtualMedicalRecord.tcm_diagnosis,
      tcm_pattern_text: virtualMedicalRecord.tcm_pattern_text,
      tcm_treatment_plan: virtualMedicalRecord.tcm_treatment_plan,
      tcm_prescription_text: virtualMedicalRecord.tcm_prescription_text,
      management_plan: virtualMedicalRecord.management_plan,
      precautions: virtualMedicalRecord.precautions,
      tcm_health: virtualMedicalRecord.tcm_health,
      created_at: now,
    };
  };

  /**
   * 从医疗记录填充虚拟病历
   */
  const populateVirtualFromRecord = (record: NormalizedRecord): void => {
    const v = (field: keyof NormalizedRecord) => String(record[field] ?? "");

    virtualMedicalRecord.chief_complaint = v("chief_complaint");
    virtualMedicalRecord.present_illness = v("present_illness");
    virtualMedicalRecord.past_history = v("past_history");
    virtualMedicalRecord.allergy_history = v("allergy_history");
    virtualMedicalRecord.physical_examination_text = v(
      "physical_examination_text",
    );
    virtualMedicalRecord.lab_results_text = v("lab_results_text");
    virtualMedicalRecord.imaging_results_text = v("imaging_results_text");
    virtualMedicalRecord.other_exams_text = v("other_exams_text");
    virtualMedicalRecord.western_diagnosis_text = v("western_diagnosis_text");
    virtualMedicalRecord.western_treatment_plan = v("western_treatment_plan");
    virtualMedicalRecord.tcm_diagnosis = v("tcm_diagnosis");
    virtualMedicalRecord.tcm_pattern_text = v("tcm_pattern_text");
    virtualMedicalRecord.tcm_treatment_plan = v("tcm_treatment_plan");
    virtualMedicalRecord.tcm_prescription_text = v("tcm_prescription_text");
    virtualMedicalRecord.management_plan = v("management_plan");
    virtualMedicalRecord.precautions = v("precautions");
    virtualMedicalRecord.tcm_health = v("tcm_health");
  };

  return {
    // State
    currentMedicalRecord,
    aiSuggested,
    recordToPopulateInput,
    currentExtractResult,
    virtualMedicalRecord,
    inputMessage,
    outputContent,
    lastLLMResponse,
    isLoading,
    isTrainingModel,
    isOptimizingPrescription,
    tcmConsultationHistory,
    westernConsultationHistory,

    // Getters
    virtualMedicalRecordFields,
    hasVirtualMedicalContent,
    hasCurrentMedicalRecord,
    currentPatientId,
    virtualRecordFilledCount,
    isAnyLoading,
    hasTcmHistory,
    hasWesternHistory,

    // Actions
    setMedicalRecord,
    setAiSuggested,
    clearAiSuggested,
    setRecordToPopulate,
    setCurrentExtractResult,
    clearCurrentExtractResult,
    setVirtualMedicalField,
    updateVirtualMedicalRecord,
    clearVirtualMedicalRecord,
    createRecordFromVirtual,
    populateVirtualFromRecord,
    setInputMessage,
    setOutputContent,
    setLastLLMResponse,
    clearInputOutput,
    setLoading,
    setTrainingModel,
    setOptimizingPrescription,
    addTcmMessage,
    addWesternMessage,
    clearTcmHistory,
    clearWesternHistory,
    clearAllConsultationHistory,
    resetAll,
  };
});

// 类型导出
export type {
  MedicalRecord,
  VirtualMedicalRecord,
  ExtractResult,
  MedicalRecordField,
  ConsultationMessage,
};
